import { Banner, Button, Code, Group, Spinner, Text } from '@optiaxiom/react';
import { AppDialog } from '@/components/ui/AppDialog';
import styles from './RegenerateWarningModal.module.css';

interface RegenerateWarningModalProps {
  open: boolean;
  /** True while the new key is being generated — shows a loader and locks the dialog. */
  loading?: boolean;
  /** True when the last attempt failed — surfaces an error and offers a retry. */
  error?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation shown before regenerating an API key — regeneration revokes the
 * current key immediately, so the user has to acknowledge the disruption first.
 * While generating, the warning is replaced by a loader and the dialog is locked
 * (can't be dismissed) until the new key is ready.
 */
export function RegenerateWarningModal({
  open,
  loading = false,
  error = false,
  onCancel,
  onConfirm,
}: RegenerateWarningModalProps) {
  return (
    <AppDialog
      contentClassName={styles.dialog}
      open={open}
      onClose={loading ? () => {} : onCancel}
      title="Regenerate API key"
      description="Regenerating a key immediately invalidates the previous one."
      footer={
        <>
          <Button appearance="subtle" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm} loading={loading} disabled={loading}>
            {error ? 'Try again' : 'Confirm'}
          </Button>
        </>
      }
    >
      {loading ? (
        <Group flexDirection="row" alignItems="center" justifyContent="center" gap="12">
          <Spinner />
          <Text color="fg.secondary">Generating your new key and revoking the old one…</Text>
        </Group>
      ) : (
        <Group flexDirection="column" gap="12" alignItems="stretch">
          <Banner intent="warning" rounded="lg">
            <Text>
              <strong>Your current key will be revoked immediately.</strong> You won&rsquo;t be able
              to use the OCP CLI commands using the old key and will start receiving{' '}
              <Code>401 Unauthorized</Code> until you swap in the new key.
            </Text>
          </Banner>
          {error && (
            <Banner intent="danger" rounded="lg">
              <Text>
                Something went wrong generating your key. Your current key is unchanged — please try
                again.
              </Text>
            </Banner>
          )}
        </Group>
      )}
    </AppDialog>
  );
}
